import { Injectable, Injector, inject } from '@angular/core';
import { AbpHttpInterceptor, MessageService } from 'abp-ng2-module';
import { HttpResponse } from '@angular/common/http';
import { Observable, switchMap, throwError } from 'rxjs';
import Swal from 'sweetalert2';
import { PiiReviewService } from './pii-review.service';

@Injectable({
    providedIn: 'root',
})
export class ZeroTemplateHttpInterceptor extends AbpHttpInterceptor {
    private messageService: MessageService = new MessageService();

    // Resolved lazily (at click time): PiiReviewService needs HttpClient, which in turn depends on
    // this interceptor, so injecting it eagerly would be a circular dependency.
    private injector: Injector = inject(Injector);

    /** Error code the backend's PII policy puts on a blocked outgoing email/SMS (PiiPolicyViolationException.PolicyErrorCode). */
    private static readonly PII_POLICY_ERROR_CODE = 7001;

    protected handleErrorResponse(error: any): Observable<never> {
        // status 0 means the request never got a response from the server at all (network down,
        // CORS failure, DNS, etc.) -- error.error here is a ProgressEvent, not a real error body.
        if (error.status === 0 || error.error == null) {
            this.messageService.error('Server is unreachable');
            return throwError(error);
        }

        if (error.error instanceof Blob) {
            // NSwag-generated service-proxies.ts clients request responseType: "blob", so the
            // error body arrives as a Blob that needs decoding before it can be parsed as JSON.
            return this.configuration.blobToText(error.error).pipe(
                switchMap((json) => {
                    const errorBody = json === '' || json === null ? {} : JSON.parse(json);
                    return this.dispatchAbpError(error, errorBody);
                }),
            );
        }

        // Hand-written services (e.g. TtwTrackingSheetService) use HttpClient's default JSON
        // responseType, so Angular has already parsed the error body into a plain object here --
        // no blob decoding needed, and skipping it is what surfaces the real UserFriendlyException
        // message (e.g. "An IWP assignment date is required...") instead of a generic fallback.
        return this.dispatchAbpError(error, error.error);
    }

    /** URL substrings for app services whose business-rule failures (a UserFriendlyException
     *  with no validation errors, e.g. "no IWP date yet") represent an expected, recoverable
     *  state rather than a real error -- shown as a warning instead of the framework's default
     *  error-style popup. Add more app-service URL fragments here as needed. */
    private static readonly WARNING_STYLE_URL_FRAGMENTS = ['/api/services/app/TtwTrackingSheet/'];

    private dispatchAbpError(error: any, errorBody: any): Observable<never> {
        const errorResponse = new HttpResponse({
            headers: error.headers,
            status: error.status,
            body: errorBody,
        });

        const ajaxResponse = this.configuration.getAbpAjaxResponseOrNull(errorResponse);

        if (ajaxResponse != null) {
            if (ajaxResponse.error?.code === ZeroTemplateHttpInterceptor.PII_POLICY_ERROR_CODE) {
                this.showPiiPolicyPopup(ajaxResponse.error);
            } else if (this.shouldUseWarningStyle(error, errorResponse, ajaxResponse)) {
                this.showWarning(ajaxResponse.error);
            } else {
                this.configuration.handleAbpResponse(errorResponse, ajaxResponse);
            }
        } else {
            this.configuration.handleNonAbpErrorResponse(errorResponse);
        }

        return throwError(error);
    }

    /**
     * The popup for an outgoing email/SMS the PII policy blocked: the usual error message, plus a
     * second button that moves the blocked message (its id travels in error.details) to the PII
     * Review Queue for an operation manager.
     */
    private showPiiPolicyPopup(errorInfo: { message?: string; details?: string }): void {
        const reviewRequestId = Number(errorInfo?.details);
        const canMoveToQueue = Number.isFinite(reviewRequestId) && reviewRequestId > 0;

        Swal.fire({
            icon: 'error',
            text: errorInfo?.message || 'This message contains restricted PII.',
            confirmButtonText: 'Edit',
            confirmButtonColor: '#6f5de7',
            showDenyButton: canMoveToQueue,
            denyButtonText: 'Move to PII Review Queue',
            denyButtonColor: '#3d5fc7',
        }).then((result) => {
            if (!result.isDenied) {
                return;
            }
            this.injector
                .get(PiiReviewService)
                .submitForReview(reviewRequestId)
                .subscribe((submitted) =>
                    this.messageService.success(
                        `The message was moved to the PII Review Queue for review. Reference: ${submitted?.piiNumber ?? ''}`.trim(),
                    ),
                );
        });
    }

    private shouldUseWarningStyle(error: any, errorResponse: HttpResponse<any>, ajaxResponse: any): boolean {
        if (ajaxResponse.success || errorResponse.status === 401) {
            return false;
        }
        const url: string = error.url || '';
        return ZeroTemplateHttpInterceptor.WARNING_STYLE_URL_FRAGMENTS.some(fragment => url.includes(fragment));
    }

    /** Mirrors AbpHttpConfigurationService.showError's message/title convention, using the
     *  warning-styled dialog instead of the error-styled one. */
    private showWarning(errorInfo: { message?: string; details?: string } | undefined): void {
        const info = errorInfo || { message: 'An error has occurred!' };
        if (info.details) {
            this.messageService.warn(info.details, info.message || 'An error has occurred!');
        } else {
            this.messageService.warn(info.message || 'An error has occurred!');
        }
    }
}
